import { Injectable } from '@angular/core';
import { UserCourse, UserCourseFilters } from '../../models/interfaces/user-course.interface';
import { environment } from '../../../../environments/environment';
import { Api } from '../api/api';
import { UserCoursesTab } from '../../models/enums/user-courses-tab.enum';
import { getCrudErrors } from '../../utils/functions';
import { Course } from '../../models/interfaces/course.interface';
import { CourseService } from '../course/course.service';
import { userCourseMock } from '../../mocks/interfaces/user-course.mock';
import { courseMock } from '../../mocks/interfaces/course.mock';
import { appendCourseFilterParams, matchesCourseFacets } from '../../utils/course-filter.util';
import { CourseProgress } from '../../models/interfaces/course-progress.interface';

const USER_NOT_ENROLLED_MESSAGE = 'O usuário não está inscrito neste curso.';

@Injectable({
  providedIn: 'root',
})
export class UserCourseService {
  constructor(private readonly courseService: CourseService) {}

  private readonly api: Api = new Api();

  private enrollments: UserCourse[] = structuredClone(userCourseMock);

  private courses: Course[] = structuredClone(courseMock);

  //#region Real API

  public async getUserCourses(_userId: string, filters: UserCourseFilters = {}): Promise<UserCourse[]> {
    if (environment.useFakeBackend) {
      return this.getUserCoursesMocked(_userId, filters);
    }

    const { error, success } = await this.api.getAsync<UserCourse[]>(this.createEnrollmentsUrl(filters));

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    return this.withEnrichedCourses(success);
  }

  public async getInProgressCourses(userId: string, filters: Omit<UserCourseFilters, 'tab'> = {}): Promise<UserCourse[]> {
    if (environment.useFakeBackend) {
      return this.getInProgressCoursesMocked(userId, filters);
    }

    return await this.getUserCourses(userId, { ...filters, tab: UserCoursesTab.IN_PROGRESS });
  }

  public async getReadyToStartCourses(
    userId: string,
    filters: Omit<UserCourseFilters, 'tab'> = {}
  ): Promise<UserCourse[]> {
    if (environment.useFakeBackend) {
      return this.getReadyToStartCoursesMocked(userId, filters);
    }

    return await this.getUserCourses(userId, { ...filters, tab: UserCoursesTab.READY_TO_START });
  }

  public async getCompletedCourses(userId: string, filters: Omit<UserCourseFilters, 'tab'> = {}): Promise<UserCourse[]> {
    if (environment.useFakeBackend) {
      return this.getCompletedCoursesMocked(userId, filters);
    }

    return await this.getUserCourses(userId, { ...filters, tab: UserCoursesTab.COMPLETED });
  }

  public async updateProgress(courseId: string, progress: number): Promise<UserCourse | undefined> {
    if (environment.useFakeBackend) {
      return this.updateProgressMocked(courseId, progress);
    }

    await this.api.putAsync(environment.routes.courses.progress(courseId), {
      progressPercentage: progress,
      timeSpent: 0,
    });

    try {
      return await this.getByUserAndCourseId('', courseId);
    } catch {
      return undefined;
    }
  }

  public async completeCourse(courseId: string): Promise<UserCourse> {
    if (environment.useFakeBackend) {
      return this.completeCourseMocked(courseId);
    }

    const { error, success } = await this.api.postAsync<CourseProgress>(
      environment.routes.courses.complete(courseId),
      {},
    );

    if (!success) {
      throw new Error(getCrudErrors(error)[0]);
    }

    const enrollment = await this.getByUserAndCourseId(success.userId, courseId);
    return this.applyProgressToEnrollment(enrollment, success);
  }

  public async enrollUserInCourse(userId: string, courseId: string): Promise<UserCourse> {
    if (environment.useFakeBackend) {
      return this.enrollUserInCourseMocked(userId, courseId);
    }

    try {
      return await this.getByUserAndCourseId(userId, courseId);
    } catch {
      // User is not enrolled yet, continue with enrollment flow.
    }

    const { error } = await this.api.postAsync(environment.routes.courses.enroll(courseId), {});

    if (error)
      throw new Error(getCrudErrors(error)[0]);

    return this.getByUserAndCourseId(userId, courseId);
  }

  public async getUserCourse(userId: string, courseId: string): Promise<UserCourse | undefined> {
    if (environment.useFakeBackend) {
      return this.getUserCourseMocked(userId, courseId);
    }

    try {
      return await this.getByUserAndCourseId(userId, courseId);
    } catch {
      return undefined;
    }
  }

  public async getByUserAndCourseId(userId: string, courseId: string): Promise<UserCourse> {
    if (environment.useFakeBackend) {
      return this.getByUserAndCourseIdMocked(userId, courseId);
    }
    const url = environment.routes.courses.one(courseId);
    const { error, success } = await this.api.getAsync<Course>(url);

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    const [enrichedCourse] = await this.courseService.enrichCoursesMetadata([success]);
    const resolvedCourse = enrichedCourse ?? success;

    return this.resolveEnrollmentForCourse(resolvedCourse, userId, async () => {
      const enrollments = await this.getUserCourses(userId);
      return enrollments.find((item) => item.courseId === courseId || item.id === courseId);
    });
  }

  //#endregion

  //#region Fake Backend

  public async getUserCoursesMocked(userId: string, filters: UserCourseFilters = {}): Promise<UserCourse[]> {
    let items = this.enrollments.filter(item => !userId || item.userId === userId);
    items = this.applyEnrollmentFilters(items, filters);
    return this.withEnrichedCourses(items);
  }

  public async getInProgressCoursesMocked(
    userId: string,
    filters: Omit<UserCourseFilters, 'tab'> = {},
  ): Promise<UserCourse[]> {
    return this.getUserCoursesMocked(userId, { ...filters, tab: UserCoursesTab.IN_PROGRESS });
  }

  public async getReadyToStartCoursesMocked(
    userId: string,
    filters: Omit<UserCourseFilters, 'tab'> = {},
  ): Promise<UserCourse[]> {
    return this.getUserCoursesMocked(userId, { ...filters, tab: UserCoursesTab.READY_TO_START });
  }

  public async getCompletedCoursesMocked(
    userId: string,
    filters: Omit<UserCourseFilters, 'tab'> = {},
  ): Promise<UserCourse[]> {
    return this.getUserCoursesMocked(userId, { ...filters, tab: UserCoursesTab.COMPLETED });
  }

  public async updateProgressMocked(courseId: string, progress: number): Promise<UserCourse | undefined> {
    const enrollment = this.enrollments.find(item => item.courseId === courseId);

    if (!enrollment) {
      return undefined;
    }

    const now = new Date();
    enrollment.progress = progress;
    enrollment.updatedAt = now;
    enrollment.lastAccessedAt = now;
    enrollment.startedAt = enrollment.startedAt ?? now;
    if (progress < 100) {
      enrollment.completedAt = undefined;
    }

    return { ...enrollment };
  }

  public async completeCourseMocked(courseId: string): Promise<UserCourse> {
    const enrollment = this.enrollments.find(item => item.courseId === courseId);

    if (!enrollment) {
      throw new Error(USER_NOT_ENROLLED_MESSAGE);
    }

    if (enrollment.completedAt) {
      throw new Error('O curso já foi completado.');
    }

    if (enrollment.progress < 100) {
      throw new Error('O usuário deve alcançar no mínimo 100% de progresso para completar o curso.');
    }

    const now = new Date();
    enrollment.progress = 100;
    enrollment.completedAt = now;
    enrollment.updatedAt = now;

    return { ...enrollment };
  }

  public async enrollUserInCourseMocked(userId: string, courseId: string): Promise<UserCourse> {
    const existing = await this.getUserCourseMocked(userId, courseId);

    if (existing) {
      return existing;
    }

    const course = this.courses.find(item => item.id === courseId)
      ?? (await this.courseService.getByIdMocked(courseId));

    if (!course) {
      throw new Error('Curso não encontrado.');
    }

    const now = new Date();
    const enrollment: UserCourse = {
      id: `user-course-${Date.now()}`,
      userId,
      courseId,
      progress: 0,
      course,
      createdAt: now,
      updatedAt: now,
      isActive: true,
    };

    this.enrollments.unshift(enrollment);
    return enrollment;
  }

  public async getUserCourseMocked(userId: string, courseId: string): Promise<UserCourse | undefined> {
    try {
      return await this.getByUserAndCourseIdMocked(userId, courseId);
    } catch {
      return undefined;
    }
  }

  public async getByUserAndCourseIdMocked(userId: string, courseId: string): Promise<UserCourse> {
    const course = await this.courseService.getByIdMocked(courseId);

    if (!course) {
      throw new Error('Curso não encontrado.');
    }

    return this.resolveEnrollmentForCourse(course, userId, async () =>
      this.enrollments.find(
        item => item.courseId === courseId && (!userId || item.userId === userId),
      ),
    );
  }

  public resetMockData(): void {
    this.enrollments = structuredClone(userCourseMock);
    this.courses = structuredClone(courseMock);
  }

  //#endregion

  private async withEnrichedCourses(items: UserCourse[]): Promise<UserCourse[]> {
    const courses = items
      .map(item => item.course ?? this.courses.find(course => course.id === item.courseId))
      .filter((course): course is Course => !!course);

    if (!courses.length) {
      return items;
    }

    const enrichedCourses = await this.courseService.enrichCoursesMetadata(courses);
    const enrichedById = new Map(enrichedCourses.map(course => [course.id, course]));

    return items.map(item => ({
      ...item,
      course: item.course
        ? (enrichedById.get(item.course.id) ?? item.course)
        : enrichedById.get(item.courseId),
    }));
  }

  private async resolveEnrollmentForCourse(
    course: Course,
    userId: string,
    findExisting: () => Promise<UserCourse | undefined> | UserCourse | undefined,
  ): Promise<UserCourse> {
    const existingEnrollment = await findExisting();

    if (userId && !existingEnrollment) {
      throw new Error(USER_NOT_ENROLLED_MESSAGE);
    }

    if (existingEnrollment) {
      return {
        ...existingEnrollment,
        userId: existingEnrollment.userId || userId,
        courseId: course.id,
        course: existingEnrollment.course ?? course,
      };
    }

    return this.mapCourseResponseToUserCourse(course, userId);
  }

  private applyEnrollmentFilters(items: UserCourse[], filters: UserCourseFilters): UserCourse[] {
    return items.filter(item => {
      const course = item.course ?? this.courses.find(entry => entry.id === item.courseId);

      if (filters.tab === UserCoursesTab.IN_PROGRESS && !(item.progress > 0 && item.progress < 100)) {
        return false;
      }

      if (filters.tab === UserCoursesTab.READY_TO_START && item.progress !== 0) {
        return false;
      }

      if (filters.tab === UserCoursesTab.COMPLETED && !item.completedAt) {
        return false;
      }

      return matchesCourseFacets(course, filters);
    });
  }

  private createEnrollmentsUrl(filters: UserCourseFilters): string {
    const params = new URLSearchParams();
    appendCourseFilterParams(params, filters);

    const query = params.toString();

    if (!query) {
      return environment.routes.courses.me;
    }

    return `${ environment.routes.courses.me }?${ query }`;
  }

  private applyProgressToEnrollment(enrollment: UserCourse, progress: CourseProgress): UserCourse {
    return {
      ...enrollment,
      progress: progress.progressPercentage,
      completedAt: progress.completedAt,
      startedAt: progress.startedAt ?? enrollment.startedAt,
      updatedAt: progress.updatedAt ?? enrollment.updatedAt,
    };
  }

  private mapCourseResponseToUserCourse(course: Course, userId = ''): UserCourse {
    return {
      id: course.id,
      userId,
      courseId: course.id,
      progress: course.progress ?? 0,
      createdAt: course.createdAt,
      updatedAt: course.updatedAt,
      isActive: course.isActive,
      course,
    };
  }
}
